import {
  Location,
  SpecialLocation,
  BilletGroup,
  TableMetadata,
  BilletsLocationsApiResponse,
  GroupTotal,
  LocationTotal,
} from "../models";

export interface ParsedLocationsData {
  locations: Location[];
  specialLocations: SpecialLocation[];
  groups: BilletGroup[];
  groupsByLocationId: Record<number, BilletGroup[]>;
  groupTotalsByLocationId: Record<number, Record<string, GroupTotal>>;
  locationTotalsById: Record<number, LocationTotal>;
}

export const parseLocationsResponse = (
  response: BilletsLocationsApiResponse,
): ParsedLocationsData | null => {
  if (!response?.ok || !response?.data?.tables?.[0]?.rows) {
    return null;
  }

  const metadata = response.data.tables[0].rows as TableMetadata[];
  const locationsIndex = metadata.find(
    (item) => item.TableName === "Locations",
  )?.TableIndex;
  const groupsIndex = metadata.find(
    (item) => item.TableName === "Groups",
  )?.TableIndex;
  const specialLocationsIndex = metadata.find(
    (item) => item.TableName === "SpecialLocations",
  )?.TableIndex;
  const groupTotalsIndex = metadata.find(
    (item) => item.TableName === "GroupTotals",
  )?.TableIndex;
  const locationTotalsIndex = metadata.find(
    (item) => item.TableName === "LocationTotals",
  )?.TableIndex;

  if (
    locationsIndex !== undefined &&
    groupsIndex !== undefined &&
    specialLocationsIndex !== undefined &&
    response.data.tables[locationsIndex] &&
    response.data.tables[groupsIndex] &&
    response.data.tables[specialLocationsIndex]
  ) {
    const locations = response.data.tables[locationsIndex].rows as Location[];
    const groups = response.data.tables[groupsIndex].rows as BilletGroup[];
    const specialLocations = response.data.tables[specialLocationsIndex]
      .rows as SpecialLocation[];

    // Process groups into lookup by LocationID
    const groupsByLocationId: Record<number, BilletGroup[]> = {};
    groups.forEach((group: BilletGroup) => {
      if (!groupsByLocationId[group.LocationID]) {
        groupsByLocationId[group.LocationID] = [];
      }
      groupsByLocationId[group.LocationID].push(group);
    });

    // Sort groups by Position DESC (matching legacy behavior)
    Object.keys(groupsByLocationId).forEach((locationId) => {
      groupsByLocationId[parseInt(locationId)] = groupsByLocationId[
        parseInt(locationId)
      ].sort((a, b) => b.Position - a.Position);
    });

    // Process group totals into lookup by LocationID and GroupName_HeatID
    const groupTotalsByLocationId: Record<
      number,
      Record<string, GroupTotal>
    > = {};
    if (
      groupTotalsIndex !== undefined &&
      response.data.tables[groupTotalsIndex]
    ) {
      const groupTotals = response.data.tables[groupTotalsIndex]
        .rows as GroupTotal[];
      groupTotals.forEach((total: GroupTotal) => {
        if (!groupTotalsByLocationId[total.LocationID]) {
          groupTotalsByLocationId[total.LocationID] = {};
        }
        // Use GroupName_HeatID as key for uniqueness
        const key = `${total.GroupName}_${total.HeatID}`;
        groupTotalsByLocationId[total.LocationID][key] = total;
      });
    }

    // Process location totals into lookup by LocationID
    const locationTotalsById: Record<number, LocationTotal> = {};
    if (
      locationTotalsIndex !== undefined &&
      response.data.tables[locationTotalsIndex]
    ) {
      const locationTotals = response.data.tables[locationTotalsIndex]
        .rows as LocationTotal[];
      locationTotals.forEach((total: LocationTotal) => {
        locationTotalsById[total.LocationID] = total;
      });
    }

    return {
      locations,
      specialLocations,
      groups,
      groupsByLocationId,
      groupTotalsByLocationId,
      locationTotalsById,
    };
  }

  return null;
};
